import { Box, Button, CircularProgress, Paper, TextField, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { SiteProfile } from '../../../types';
import { useDispatch, useSelector } from 'react-redux';
import { Selectors } from '../../../selectors';
import { Actions, Thunks } from '../../../actions';
import { useToasts } from '../../../data/DataProviders/ToastProvider';
import { useAuthHelper } from '../../../components/AuthContextProvider';
import { isValidCharWithoutSpace } from '../../../lib/site-helper';
import locationStyles from '../LocationStyle';
import { NavigationPromptModal } from '../../../components/NavigationPromptModal/NavigationPromptModal';
import { HeaderInfo } from '../../../components/HeaderInfo/HeaderInfo';
import { useCurrentAppRoute } from '../../../hooks/app-route-hooks';
import { pushAppRoute } from '../../../data/domain/route-manager';
import { useHistory } from 'react-router-dom';
import { useLanguageLocale } from '../../../hooks/language-locale-hooks';
import { ContentContainer } from '../../../components/ContentContainer';
import { GenericToolbar } from '../../../components/GenericToolbar';
import { countries, states } from '../../../constants/state-list';

export const EditLocation: React.FC<{
    updatedLocationData: Function;
    data: SiteProfile;
}> = (props) => {
    const siteState = useSelector(Selectors.Site.site);
    const dispatch = useDispatch();
    const history = useHistory();
    const { addToast } = useToasts();
    const authHelper = useAuthHelper();
    const classes = locationStyles();
    const { t } = useLanguageLocale();
    const currentAppRoute = useCurrentAppRoute();
    const [locationInfo, setLocationInfo] = useState<SiteProfile>(props.data);
    const [unmodifiedLocationInfo] = useState<SiteProfile>(props.data);
    const [isDirty, setIsDirty] = useState(false);
    const [selectedCountry, setSelectedCountry] = React.useState<string>('');
    const [selectedState, setSelectedState] = useState<string>('');

    const navigateToLocationProfile = (): void => {
        if (currentAppRoute.type === 'EditLocation')
            pushAppRoute(history, {
                type: 'LocationProfile',
                orgId: currentAppRoute.orgId,
                locationId: currentAppRoute.locationId,
            });
    };

    useEffect(() => {
        if (siteState.success) {
            props.updatedLocationData(locationInfo);
        }
    }, [siteState.success]);

    useEffect(() => {
        if (!siteState.loading && siteState.success) {
            addToast('Location updated successfully.');
            dispatch(Actions.Site.SiteUpdateReset());
            dispatch(Thunks.Dashboard.locationRefresh(true));
            navigateToLocationProfile();
        }
    }, [siteState.loading, siteState.success]); //eslint-disable-line react-hooks/exhaustive-deps

    const countryValue = countries.find(
        (x) => x.value === locationInfo.country
    );


    const stateValue = countryValue && states[countryValue.value].find(
        (x: { value: string }) => x.value === locationInfo.state

    )

    useEffect(() => {
        const isDataModified = JSON.stringify(unmodifiedLocationInfo) !== JSON.stringify(locationInfo);
        setIsDirty(isDataModified);
    }, [unmodifiedLocationInfo, locationInfo]); //eslint-disable-line react-hooks/exhaustive-deps

    const isDisable = (): boolean => {
        let result = false;
        result =
            locationInfo.name.trim() === '' ||
            locationInfo.address.trim() === '' ||
            locationInfo.city.trim() === '' ||
            locationInfo.country.trim() === '' ||
            locationInfo.state.trim() === '' ||
            locationInfo.zipCode.trim() === '' ||
            (countryValue && countryValue.value === 'US' ? locationInfo.zipCode.length !== 5 : locationInfo.zipCode.length !== 6) ||
            !isDirty;
        return result;
    };

    const onChangeLocationName = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
        modifiedlocationName: SiteProfile
    ): void => {
        if (isValidCharWithoutSpace(e.target.value)) {
            const locationNameObj: SiteProfile = { ...modifiedlocationName };
            locationNameObj.name = e.target.value;
            setLocationInfo(locationNameObj);
        }
    };
    const onChangeAddress = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
        modifiedAddress: SiteProfile
    ): void => {
        if (isValidCharWithoutSpace(e.target.value)) {
            const addressObj: SiteProfile = { ...modifiedAddress };
            addressObj.address = e.target.value;
            setLocationInfo(addressObj);
        }
    };
    const onChangeCity = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
        modifiedCity: SiteProfile
    ): void => {
        if (isValidCharWithoutSpace(e.target.value)) {
            const citysObj: SiteProfile = { ...modifiedCity };
            citysObj.city = e.target.value;
            setLocationInfo(citysObj);
        }
    };
    const handleCountryChange = (
        event: any, modifiedState: SiteProfile): void => {
        const country = event.target.value;
        const countryObj: SiteProfile = { ...modifiedState };
        countryObj.country = country || '';
        setLocationInfo(countryObj);
        setSelectedCountry(country);
    };
    const handleStateChange = (event: any, modifiedState: SiteProfile): void => {
        const state = event.target.value;
        const stateObj: SiteProfile = { ...modifiedState };
        stateObj.state = state || '';
        setLocationInfo(stateObj);
        setSelectedState(state)
    }
    const onChangeZipCode = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
        modifiedZipCode: SiteProfile
    ): void => {
        if (isValidCharWithoutSpace(e.target.value)) {
            const zipCodeObj: SiteProfile = { ...modifiedZipCode };
            zipCodeObj.zipCode = e.target.value;
            setLocationInfo(zipCodeObj);
        }
    };
    const onChangeNotes = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
        modifiedNotes: SiteProfile
    ): void => {
        if (isValidCharWithoutSpace(e.target.value)) {
            const notesObj: SiteProfile = { ...modifiedNotes };
            notesObj.description = e.target.value;
            setLocationInfo(notesObj);
        }
    };

    const submit = (): void => {
        setIsDirty(false);
        dispatch(Thunks.Site.updateSiteRegistration(locationInfo.id, locationInfo, addToast, authHelper));
        dispatch(Thunks.Site.getExludedStateResponse(selectedState, selectedCountry, addToast, authHelper));

    };
    return (
        <>
            <GenericToolbar
                title={locationInfo.name}
                subtitle="Location Profile"
                showBackButton
                backButtonOnClick={navigateToLocationProfile}
            />
            <ContentContainer>
                <Paper style={{ padding: 48 }}>
                    <HeaderInfo title="Location Information" entity="when editing a Location" />
                    <Box display="flex" flexDirection="column" alignItems="left">
                        <Box className={classes.paddingTextbox}>
                            <TextField
                                inputProps={{ 'data-testid': 'location-name', maxLength: 100 }}
                                fullWidth
                                required
                                variant="filled"
                                id="location"
                                label="Location Name"
                                value={locationInfo.name}
                                onChange={(e): void => onChangeLocationName(e, locationInfo)}
                                helperText={
                                    <Box component="span" display="flex" justifyContent="flex-end">
                                        {locationInfo.name.length}/100
                                    </Box>
                                }
                            />
                        </Box>
                        <Box className={classes.paddingTextbox} style={{ paddingBottom: 24, paddingTop: 0 }}>
                            <TextField
                                inputProps={{ 'data-testid': 'Address', maxLength: 255 }}
                                fullWidth
                                required
                                variant="filled"
                                id="Address"
                                label="Address"
                                value={locationInfo.address}
                                onChange={(e): void => {
                                    onChangeAddress(e, locationInfo);
                                }}
                            />
                        </Box>
                        <Box
                            className={`${classes.padding8} ${classes.paddingTop16} ${classes.paddingBottom24}`}
                            display="flex"
                            justifyContent="space-between"
                        >
                            <TextField
                                inputProps={{ 'data-testid': 'city', maxLength: 100 }}
                                style={{ width: '55%' }}
                                required
                                variant="filled"
                                id="City"
                                label="City"
                                value={locationInfo.city}
                                onChange={(e): void => {
                                    onChangeCity(e, locationInfo);
                                }}
                            />
                            <FormControl variant="filled" required>
                                <InputLabel id="Data" data-testid="label" >
                                    Country
                                </InputLabel>
                                <Select
                                    // style={{ width: '55%' }}
                                    label="Country"
                                    style={{ width: 400, padding: '0px 16px' }}
                                    required
                                    // inputProps={{
                                    //     'data-testid': 'role-dd',
                                    // }}
                                    //fullWidth
                                    // onChange={(e): void => {
                                    //     handleChangeAddRole(e, props.roleData, index);
                                    // }}
                                    //value={selectedCountry}
                                    value={countryValue && countryValue.value}
                                    //onChange={handleCountryChange}
                                    onChange={(e): void => {
                                        handleCountryChange(e, locationInfo);
                                    }
                                    }

                                //key={index}

                                >
                                    {countries.map((country) => (
                                        <MenuItem data-testid="country-menu" key={country.value} value={country.value}>
                                            {country.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>
                        <Box
                            className={`${classes.padding8} ${classes.paddingTop16} ${classes.paddingBottom24}`}
                            display="flex"
                            justifyContent="space-between"
                        >
                            <FormControl variant="filled" required>
                                <InputLabel id="Data1" data-testid="label1" >
                                    State
                                </InputLabel>
                                <Select
                                    // label="State"
                                    style={{ width: 560, padding: '0px 16px' }}
                                    required
                                    value={stateValue && stateValue.value}
                                    //onChange={handleStateChange}
                                    onChange={(e): void => {
                                        handleStateChange(e, locationInfo);
                                    }}
                                >
                                    {countryValue && states[countryValue.value].map((item: any) => (
                                        //return (
                                        //stateValue && stateValue.value === state.value?
                                        <MenuItem data-testid="state-menu" value={item.value} key={item.value}>
                                            {item.label}
                                        </MenuItem>
                                        //:''
                                        //);
                                    ))}
                                </Select>
                            </FormControl>
                            <TextField
                                inputProps={{ 'data-testid': 'zipCode', maxLength: countryValue && countryValue.value === 'US' ? 5 : 6 }}
                                style={{ width: '39%', margin: '0px 5px' }}
                                required
                                variant="filled"
                                id="Zip Code"
                                label="Zip Code"
                                value={locationInfo.zipCode}
                                onChange={(e): void => {
                                    onChangeZipCode(e, locationInfo);
                                }}
                            />
                        </Box>
                        <Box className={classes.paddingTextbox}>
                            <TextField
                                inputProps={{ 'data-testid': 'notes', maxLength: 255 }}
                                variant="filled"
                                id="Notes"
                                multiline
                                maxRows={Infinity}
                                label="Notes"
                                fullWidth
                                value={locationInfo.description}
                                onChange={(e): void => {
                                    onChangeNotes(e, locationInfo);
                                }}
                                helperText={
                                    <Box component="span" display="flex" justifyContent="flex-end">
                                        {locationInfo.description ? locationInfo.description.length : 0}/255
                                    </Box>
                                }
                            />
                        </Box>
                        <Box className={classes.iconRightEnd}>
                            <Button
                                style={{ marginRight: 16 }}
                                variant="outlined"
                                color="primary"
                                data-testid="cancel-location"
                                onClick={navigateToLocationProfile}
                            >
                                {t('ACTIONS.CANCEL')}
                            </Button>
                            <Button
                                startIcon={
                                    siteState.loading ? (
                                        <CircularProgress size="1rem" style={{ color: 'white' }} />
                                    ) : (
                                        <></>
                                    )
                                }
                                variant="contained"
                                disabled={isDisable()}
                                color="primary"
                                data-testid="location-update"
                                onClick={submit}
                            >
                                {t('ACTIONS.SUBMITCHANGES')}
                            </Button>
                        </Box>
                    </Box>
                </Paper>
                <NavigationPromptModal isDirty={isDirty} />
            </ContentContainer>
        </>
    );
};
